import { useEffect, useState } from "react";
import get from "lodash/get";
import gql from "graphql-tag";
import { useQuery, useMutation } from "@apollo/react-hooks";

import {
	OKEVENT,
	triggerOkEvent,
} from "shared/util/OkEventHelpers";

export const GET_BOOST_INFO_QUERY = gql`
	query boostInfo($userid: String!) {
		user(id: $userid) {
			id
			activeBoost {
				id
				startTime
				endTime
				viewerCount
				attentionMultiplier
			}
			boostTokenCount
			lastBoost {
				id
				viewerCount
			}
		}
	}
`;

export const START_BOOST_MUTATION = gql`
  mutation startBoost {
		startBoost {
			user {
				id
				activeBoost {
					id
					startTime
					endTime
					viewerCount
					attentionMultiplier
				}
				boostTokenCount
			}
		}
  }
`;

export const END_BOOST_MUTATION = gql`
  mutation endBoost {
		endBoost {
			user {
				id
				activeBoost {
					id
					startTime
					endTime
					viewerCount
					attentionMultiplier
				}
				boostTokenCount
			}
		}
  }
`;

// How often to refetch boost info while boosting.
const BOOST_INFO_REFETCH_INTERVAL = 10000;

function useBoostController({ userid, onBoostStart, onBoostEnd }) {
	const [hasActiveBoost, setHasActiveBoost] = useState(false);
	const [startBoost] = useMutation(START_BOOST_MUTATION);
	const [endBoost] = useMutation(END_BOOST_MUTATION);

	// Fetch user information if they're logged in.
	const { data, loading } = useQuery(GET_BOOST_INFO_QUERY, {
		variables: { userid },
		pollInterval: hasActiveBoost ? BOOST_INFO_REFETCH_INTERVAL : 0,
		skip: !userid,
	});
	const activeBoost = get(data, "user.activeBoost");
	const lastBoost = get(data, "user.lastBoost");
	const boostTokenCount = get(data, "user.boostTokenCount");
	const [hasData, setHasData] = useState(false);

	// Finished the initial load.
	if (!hasData && !loading && data) {
		setHasData(true);
	}

	// Trigger some boost-related okevents.
	useEffect(() => {
		// Boost just started.
		if (!hasActiveBoost && activeBoost) {
			setHasActiveBoost(true);
			triggerOkEvent(OKEVENT.BoostStarted);

			if (onBoostStart) {
				onBoostStart();
			}
		}

		// Boost just ended.
		if (hasActiveBoost && !activeBoost) {
			setHasActiveBoost(false);
			triggerOkEvent(OKEVENT.BoostEnded);

			if (onBoostEnd) {
				onBoostEnd();
			}
		}
	}, [activeBoost, hasActiveBoost, onBoostEnd, onBoostStart]);

	return {
		activeBoost,
		lastBoost,
		boostTokenCount,

		startBoost,
		endBoost,

		hasData,
	};
}

export default useBoostController;



// WEBPACK FOOTER //
// ./src/shared/hooks/useBoostController.js